import {Logger, StringUtils, Utils, StringsFactory} from 'neo-common';
import {registerClientHost} from '../ClientHostFactory';
import {ClientHostBase} from '../ClientHostBase';
import {
    GameActions,
    ClientHostPaytableRequest,
    ClientHostVolumeData,
    ClientHostMetadataTypes,
    ClientHostEndGameRequest,
    ClientHostDialogRequest,
    IClientHostSupportFlags,
    IGameEventData, OnOffState
} from '../Types';
import {IClientHostConsumer} from '../IClientHostConsumer';
import {IApp} from '../../GameEngine/IApp';
import {IGameConfigurationProxy} from '../../GameEngine/Types';
import {Platforms} from '../../Platforms';

const version = '1';
const typeName = 'Default';

@registerClientHost(typeName)
export class ClientHostDefault extends ClientHostBase {
    private _backgroundMusic: OnOffState;

    constructor(clientHostConsumer: IClientHostConsumer) {
        super(clientHostConsumer);

        this._backgroundMusic = OnOffState.On;
    }

    protected getLogger(): string {
        return 'ClientHostDefault';
    }

    public get version(): string {
        return version;
    }

    public get type(): string {
        return typeName;
    }

    public get supports(): IClientHostSupportFlags {
        return {
            paytable: false,
            volume: false,
            serverConfigurationRequestAllowed: true,
            denomination: false,
            settings: false,
            externalMessage: false
        };
    }

    protected doInitialize(): Promise<any> {
        return Promise.resolve();
    }

    actionsUpdated(actions: GameActions): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `Received actionsUpdated: `, actions);
        }
    }

    endGame(app: IApp, request: ClientHostEndGameRequest): void {
        let exitUrl = app.gameConfiguration.ExitUrl;

        if (Platforms.handleGameExit) {
            return;
        }

        if (!StringUtils.isNullOrEmpty(exitUrl)) {
            if (DEBUG) {
                Logger.debugGroup(this.getLogger(), `endGame() - Redirecting to exit url: `, exitUrl);
            }

            window.location.replace(exitUrl);
        } else if (window.history.length > 1) {
            if (DEBUG) {
                Logger.warnGroup(this.getLogger(), `endGame() - Exit url was NOT found in game configuration, will do browser history back.`);
            }

            window.history.go(-1);
        } else {
            this.showEndGameDialog();
        }
    }

    public history(app: IApp): void {
        let historyUrl = app.gameConfiguration.HistoryUrl;

        if (!StringUtils.isNullOrEmpty(historyUrl)) {
            if (DEBUG) {
                Logger.debugGroup(this.getLogger(), `Redirecting to history url: `, historyUrl);
            }

            window.open(historyUrl, '_blank');
        } else {
            if (DEBUG) {
                Logger.warnGroup(this.getLogger(), `History url was NOT found in game configuration, will do nothing.`);
            }
        }
    }

    public continue(app: IApp): void {
    }

    public showDialog(request: ClientHostDialogRequest): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `showDialog(), request: `, request);
        }

        // for this client host, just forward 'showDialog' to client host consumer
        this._clientHostConsumer.handleShowDialog(request);
    }

    public showEndGameDialog(): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `handleShowEndGameDialog()`);
        }

        // for this client host, just forward 'handleShowEndGameDialog' to client host consumer
        this._clientHostConsumer.handleShowEndGameDialog();
    }

    public closeDialog(): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `closeDialog()`);
        }

        // for this client host, just forward 'closeDialog' to client host consumer
        this._clientHostConsumer.handleCloseDialog();
    }

    sendRequestToPaytable(request: ClientHostPaytableRequest): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(),
                `received event "sendRequestToPaytable" request: ${JSON.stringify(request)}`);
        }
    }

    public getVolume(): ClientHostVolumeData {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(),
                `getVolume() returns null (does not support 'volume' feature)`);
        }

        return null;
    }

    /**
     * @method setVolume - sets current volume within client host.
     * @param stepNo - volume setting represented by step number from range 0-3.
     */
    public setVolume(stepNo: number): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(),
                `setVolume() does nothing (does not support 'volume' feature)`);
        }
    }

    public getMetadata<T>(type: ClientHostMetadataTypes): T {
        switch (type) {
            case ClientHostMetadataTypes.BackgroundMusic:
                return <any>true;
            case ClientHostMetadataTypes.MuteVolumeAllowed:
                return <any>true;
        }

        return undefined;
    }

    public gameLoaded(app: IApp): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `inside gameLoaded(), loadingSucceed = `, app.loadingSucceed);
        }
    }

    public getLanguage(): string {
        const query = Utils.getURIQuery();
        return Utils.valueOrDefault(query['lang'], '');
    }

    /**
     * Gets array of allowed languages.
     * @returns {Array<string>}
     */
    public getAllowedLanguages(): Array<string> {
        return StringsFactory.getLanguages();
    }

    /**
     * Gets current denomination value.
     * @returns {number}
     */
    public getDenomination(): number {
        return null;
    }

    public setDenomination(denomination: number): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `setDenomination(${denomination}) called.`);
        }
    }

    public setLanguage(language: string): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `setLanguage('${language}') called.`);
        }
    }

    public notifyEventFromGame(eventName: string, eventData?: any): void;
    public notifyEventFromGame(eventData: IGameEventData): void;
    public notifyEventFromGame(eventName: string | IGameEventData, eventData?: any): void {
        if (DEBUG) {
            let data = '-';
            if (!Utils.isNullOrUndefined(eventData)) {
                data = JSON.stringify(eventData);
            }

            Logger.debugGroup(this.getLogger(), `notifyEventFromGame(evetName: ${JSON.stringify(eventName)}, eventData: ${data}') called.`);
        }
    }

    public getBackgroundMusic(): OnOffState {
        return this._backgroundMusic;
    }

    public getGameSetting(key: string): string {
        return undefined;
    }

    public getSharedSetting(key: string): string {
        return undefined;
    }

    public setBackgroundMusic(value: OnOffState): void {
        this._backgroundMusic = value;
    }

    public updateGameSetting(key: string, value: string): void {
        throw new Error('Not supported - updateGameSetting');
    }

    public updateSharedSetting(key: string, value: string): void {
        throw new Error('Not supported - updateSharedSetting');
    }

    public setLEDStripe(rgb: string, mode: number): void {
        throw new Error('Not supported - setLEDStripe');
    }

    public updateGameConfiguration(gameConfiguration: IGameConfigurationProxy): Promise<void> {
        return Promise.resolve();
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/ClientHost/Common/ClientHostDefault.ts
 **/